'use client'

import { useCallback, useEffect, useRef } from 'react'
import clsx from 'clsx'

import { CartAddressOption, useCartStore } from 'modules/cart'
import { ModalAddInvoiceAddress } from 'modules/customer'
import { FormMode } from 'shared/types/forms'
import Button from 'shared/ui/Button'
import Checkbox from 'shared/ui/form/Checkbox'
import type { CheckboxProps } from 'shared/ui/form/Checkbox/Checkbox'
import { Listbox, ListboxItem } from 'shared/ui/Listbox'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import { cssColors } from 'shared/ui/utils/cssTokens'

import { useCartInvoiceSelection } from '../hooks/useCartInvoiceSelection'

import styles from './CartAddInvoiceAddress.module.scss'

const CartAddInvoiceAddress = () => {
  const initiated = useRef(false)

  const { cart } = useCartStore()
  const {
    invoiceData,
    setInvoiceData,
    addresses,
    isLoadingInvoice,
    isPendingInvoice,
    initStoredInvoiceData
  } = useCartInvoiceSelection()

  useEffect(() => {
    if (!cart || initiated.current || isLoadingInvoice) return

    initiated.current = true

    if (!cart.invoiceData?.id) return

    void initStoredInvoiceData()
  }, [cart, initStoredInvoiceData, isLoadingInvoice])

  const wantsInvoiceToggle = useCallback(
    () =>
      void setInvoiceData(prev => ({
        ...prev,
        wantsInvoice: !prev.wantsInvoice
      })),
    [setInvoiceData]
  )

  const handleAssignAddressToCart = useCallback(
    (addressId: string) => {
      if (invoiceData.selectedAddressId !== addressId) {
        void setInvoiceData({
          wantsInvoice: true,
          selectedAddressId: addressId
        })
      }
    },
    [setInvoiceData, invoiceData]
  )

  const hasAddresses = addresses.length > 0
  const isLoading = isLoadingInvoice || isPendingInvoice

  return (
    <>
      <div className={styles.title}>Potrzebujesz faktury do zamówienia?</div>
      <CustomCheckbox
        name='wantsInvoice'
        onChange={wantsInvoiceToggle}
        value={invoiceData.wantsInvoice}
        label='Chcę otrzymać fakturę'
        aria-label={isLoading ? 'Ładowanie...' : 'Chcę otrzymać fakturę'}
        disabled={isLoading}
      />

      {invoiceData.wantsInvoice ? (
        isLoading ? (
          <LoadingIndicator color={cssColors.brand.primary} size={40} />
        ) : (
          <div className={styles.wrapper}>
            {hasAddresses ? (
              <Listbox
                id='invoice-addresses-list'
                aria-label='Lista adresów faktur'
                selectedOptionId={invoiceData.selectedAddressId}
                onSelect={handleAssignAddressToCart}
              >
                {addresses?.map(el => {
                  const checked = invoiceData.selectedAddressId === el.id

                  return (
                    <ListboxItem key={el.id} optionId={el.id}>
                      <CartAddressOption invoiceAddress={el} checked={checked} />
                    </ListboxItem>
                  )
                })}
              </Listbox>
            ) : (
              <p className={styles.noItemsMessage}>
                Nie dodano jeszcze żadnych podmiotów.
              </p>
            )}
            <ModalAddInvoiceAddress
              mode={FormMode.create}
              trigger={
                <Button fullWidth size='medium' variant='secondary'>
                  Dodaj nowy podmiot
                </Button>
              }
            />
          </div>
        )
      ) : null}
    </>
  )
}

export default CartAddInvoiceAddress

export const CustomCheckbox = ({ className, ...props }: CheckboxProps) => (
  <Checkbox {...props} className={clsx(styles.customCheckbox, className)} />
)
